import React, { useState, useEffect } from 'react';
import api from '../api';

function Send() {
  const [step, setStep] = useState('form'); // 'form' | 'review' | 'success'
  const [currency, setCurrency] = useState('BTC');
  const [amount, setAmount] = useState('');
  const [recipient, setRecipient] = useState('');
  const [error, setError] = useState('');
  const [requestDetails, setRequestDetails] = useState(null);
  const [userRequests, setUserRequests] = useState([]);
  const [withdrawalMessage, setWithdrawalMessage] = useState('');   // ✅ added

  useEffect(() => {
    // fetch user's existing withdrawal requests
    api.get('/api/send/my-requests')
      .then(res => setUserRequests(res.data))
      .catch(err => console.error('Fetch requests error:', err));

    // fetch user profile to get the default withdrawal message
    api.get('/api/user/profile')
      .then(res => {
        if (res.data.withdrawalMessage) {
          setWithdrawalMessage(res.data.withdrawalMessage);
        }
      })
      .catch(err => console.error('Fetch profile error:', err));
  }, []);

  // Step 1 → Step 2
  const goToReview = (e) => {
    e.preventDefault();
    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (!recipient.trim()) {
      setError('Please enter a recipient address');
      return;
    }
    setError('');
    setStep('review');
  };

  // Step 2 → Submit → Step 3
  const confirmSend = async () => {
    try {
      const res = await api.post('/api/send/request', {
        currency,
        amount: parseFloat(amount),
        recipientAddress: recipient,
      });
      setRequestDetails(res.data);
      setStep('success');
      // refresh history
      const list = await api.get('/api/send/my-requests');
      setUserRequests(list.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Request failed');
      setStep('form'); // back to form on error
    }
  };

  // Reset form to make another request
  const makeAnother = () => {
    setStep('form');
    setAmount('');
    setRecipient('');
    setCurrency('BTC');
    setError('');
  };

  // Compact success view
  if (step === 'success' && requestDetails) {
    return (
      <div>
        <h2>Send Crypto</h2>
        <div className="card" style={{ maxWidth: '450px', margin: '0 auto', textAlign: 'center', background: '#f0fdf4', border: '1px solid #10b981' }}>
          <h3 style={{ color: '#10b981', marginBottom: '0.5rem' }}>✅ Request Submitted</h3>
          <p style={{ margin: '0.3rem 0' }}><strong>{requestDetails.amount} {requestDetails.currency}</strong></p>
          <p style={{ margin: '0.3rem 0', fontSize: '0.9rem', wordBreak: 'break-all' }}>To: {requestDetails.recipientAddress}</p>
          <p style={{ margin: '0.3rem 0', fontSize: '0.85rem', color: '#f59e0b' }}>Status: Pending Admin Approval</p>

          {/* 🔔 Default withdrawal message from admin – only on success */}
          {withdrawalMessage && (
            <div style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              padding: '0.8rem',
              borderRadius: '0.5rem',
              marginTop: '1rem',
              color: '#856404',
              fontWeight: 500,
              textAlign: 'left',
            }}>
              ⚠️ {withdrawalMessage}
            </div>
          )}

          <button onClick={makeAnother} style={{ marginTop: '1rem', background: '#3b82f6', color: 'white', border: 'none', padding: '0.5rem 1.5rem', borderRadius: '0.5rem', cursor: 'pointer' }}>
            Make Another Request
          </button>
        </div>

        <h3 style={{ marginTop: '2rem' }}>Your Withdrawal History</h3>
        {userRequests.length === 0 ? <p>No requests yet.</p> : (
          <div className="table-box">
            <table>
              <thead><tr><th>Currency</th><th>Amount</th><th>To</th><th>Status</th><th>Fee</th><th>Message</th></tr></thead>
              <tbody>
                {userRequests.map(req => (
                  <tr key={req.id}>
                    <td>{req.currency}</td>
                    <td>{req.amount}</td>
                    <td>{req.recipientAddress?.slice(0,10)}...</td>
                    <td style={{ color: req.status === 'approved' ? 'green' : req.status === 'rejected' ? 'red' : 'orange' }}>{req.status}</td>
                    <td>{req.networkFee ?? '—'}</td>
                    <td>{req.adminMessage || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // Review step (Step 2)
  if (step === 'review') {
    return (
      <div>
        <h2>Send Crypto</h2>
        <div className="card" style={{ maxWidth: '500px', margin: '0 auto' }}>
          <h3>Transaction Request</h3>
          <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>Please review the details before confirming.</p>

          <div style={{ marginBottom: '1rem' }}>
            <strong>Currency:</strong> {currency}
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <strong>Amount:</strong> {amount} {currency}
          </div>
          <div style={{ marginBottom: '1rem', wordBreak: 'break-all' }}>
            <strong>Recipient:</strong><br/>
            <span style={{ fontSize: '0.9rem', color: '#475569' }}>{recipient}</span>
          </div>
          <div style={{ marginBottom: '1.5rem' }}>
            <strong>Estimated Network Fee:</strong> TBD (will be set by admin)
          </div>

          {error && <p style={{ color: 'red' }}>{error}</p>}

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              onClick={() => { setStep('form'); setError(''); }}
              style={{ flex: 1, background: '#e2e8f0', color: '#334155', border: 'none', padding: '0.75rem', borderRadius: '0.5rem', fontWeight: 600, cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              onClick={confirmSend}
              style={{ flex: 1, background: '#3b82f6', color: 'white', border: 'none', padding: '0.75rem', borderRadius: '0.5rem', fontWeight: 600, cursor: 'pointer' }}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 1 – form
  return (
    <div>
      <h2>Send Crypto</h2>
      <div className="card" style={{ maxWidth: '500px', margin: '0 auto' }}>
        <form onSubmit={goToReview}>
          <label>Currency</label>
          <select value={currency} onChange={e => setCurrency(e.target.value)} required
            style={{ width: '100%', padding: '0.75rem', marginBottom: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}>
            <option value="BTC">Bitcoin (BTC)</option>
            <option value="ETH">Ethereum (ETH)</option>
            <option value="USDT">Tether (USDT)</option>
          </select>

          <label>Recipient Address</label>
          <input type="text" placeholder="Enter wallet address" value={recipient}
            onChange={e => setRecipient(e.target.value)} required
            style={{ width: '100%', padding: '0.75rem', marginBottom: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }} />

          <label>Amount</label>
          <input type="number" placeholder="0.00" value={amount}
            onChange={e => setAmount(e.target.value)} required
            style={{ width: '100%', padding: '0.75rem', marginBottom: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }} />

          {error && <p style={{ color: 'red' }}>{error}</p>}

          <button type="submit" style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '0.5rem', fontWeight: 600, cursor: 'pointer', width: '100%' }}>
            Continue
          </button>
        </form>
      </div>
    </div>
  );
}

export default Send;